import React, { useState, useEffect, useRef } from "react";
import { CheckCircle2, RotateCcw, Trash2, X, Plus, Zap, ShoppingBag, Star, CheckSquare, Edit2, Save, ArrowDown, Trophy } from "lucide-react";
import { doc, deleteDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase"; 

export default function Todo({
  userName, 
  todos = [],
  newTodoText, 
  setNewTodoText,
  newTodoPriority,
  setNewTodoPriority, 
  newTodoType, 
  setNewTodoType,
  isDarkMode,
  handleAddTodo, 
  toggleTodoStatus, 
  renderHeroShell, 
  clearCompletedTodos,
  openGlobalAction
}) {
  const [activeModalTodo, setActiveModalTodo] = useState(null);
  const [editTaskData, setEditTaskData] = useState({ text: "", priority: 3, type: "task", emoji: "📝" });
  const [isMounted, setIsMounted] = useState(false);

  const [newTodoEmoji, setNewTodoEmoji] = useState("📝");
  const [isIconSelectorOpen, setIsIconSelectorOpen] = useState(false);
  const [isEditIconSelectorOpen, setIsEditIconSelectorOpen] = useState(false);
  
  const addTaskRef = useRef(null);
  const editTaskRef = useRef(null);
  
  const categoryEmojis = [
    "📝", "📋", "📁", "📌", "✏️", "⚙️", "🔧", "💻", "📱", "💼", "✉️", "📧", "📞", "📅", "🚀", "🎯", "💡", "⚡", "🔥",
    "💵", "💰", "💳", "🏦", "📈", "💎", "🧾", "🛍️", "🛒", "📦",
    "🏃‍♂️", "🧘", "🏋️‍♂️", "🚴‍♂️", "🥗", "💊", "🏥", "🩺", "💧", "☕", "🍔", "🍕", "🍎", "🥑",
    "🏠", "🧹", "🧺", "🧼", "🔑", "🛋️", "🐶", "🐱", "🌱", "🪴", "🎁", "🎓", "📚", "🎨", "🎮", "🎸", "🎧",
    "🚗", "🏍️", "🚲", "✈️", "⛵", "🧳", "🗺️", "🏖️", "🏨", "⛽",
    "⭐", "🌟", "🛑", "⚠️", "🔔", "⏰", "⏳", "🔒", "🛡️", "🏆", "🎉", "✨", "✅"
  ];

  const sortTasks = (tasks) => tasks.sort((a, b) => parseInt(b.priority || 1) - parseInt(a.priority || 1));

  const pendingActions = sortTasks(todos.filter(t => !t.isCompleted && t.type === "task"));
  const pendingShopping = sortTasks(todos.filter(t => !t.isCompleted && t.type === "shopping"));
  const completedTasks = sortTasks(todos.filter(t => t.isCompleted));

  const totalTasks = todos.length;
  const completedCount = completedTasks.length;
  const momentumPct = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const isVictoryState = totalTasks > 0 && momentumPct === 100;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (momentumPct / 100) * circumference;

  let momentumBadge = { text: "GET STARTED 🚀", classes: isDarkMode ? "text-slate-400 border-slate-700" : "text-slate-500 border-slate-200" };
  if (momentumPct > 0 && momentumPct < 50) momentumBadge = { text: "BUILDING STEAM ⚡", classes: "text-blue-500 border-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.3)] bg-blue-500/10" };
  if (momentumPct >= 50 && momentumPct < 100) momentumBadge = { text: "IN THE ZONE 🔥", classes: "text-fuchsia-500 border-fuchsia-500/50 shadow-[0_0_12px_rgba(217,70,239,0.3)] bg-fuchsia-500/10" };
  if (isVictoryState) momentumBadge = { text: "DOMINATING 🏆", classes: "text-emerald-500 border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.4)] bg-emerald-500/10" };

  const triggerHaptic = () => { 
    if (typeof window !== "undefined" && window.navigator && window.navigator.vibrate) window.navigator.vibrate(50); 
  };

  const handleDeleteTask = () => {
    if (!auth.currentUser || !activeModalTodo) return;
    
    openGlobalAction(
      "Delete Task", 
      "Are you sure you want to permanently delete this task?", 
      "Delete", 
      true, 
      async () => {
        try {
          await deleteDoc(doc(db, "users", auth.currentUser.uid, "todos", activeModalTodo.id));
          triggerHaptic();
          setActiveModalTodo(null);
          
          setTimeout(() => {
            const cancelBtns = Array.from(document.querySelectorAll('button')).filter(btn => btn.textContent.trim().toUpperCase() === "CANCEL");
            if (cancelBtns.length > 0) cancelBtns[0].click();
          }, 50);
        } catch (error) {
          console.error("Error deleting task:", error);
        }
      }
    );
  };

  const handleSaveEdit = async () => {
    if (!auth.currentUser || !activeModalTodo || !editTaskData.text.trim()) return;
    
    await updateDoc(doc(db, "users", auth.currentUser.uid, "todos", activeModalTodo.id), {
        text: editTaskData.text,
        priority: editTaskData.priority,
        type: editTaskData.type,
        emoji: editTaskData.emoji
    });
    
    triggerHaptic();
    setActiveModalTodo({ ...activeModalTodo, ...editTaskData });
    setActiveModalTodo(null);
  };

  const executeAddTodo = (e) => {
    e.preventDefault();
    handleAddTodo(e, newTodoEmoji);
    setNewTodoEmoji("📝");
  };

  const renderStars = (priorityNum) => {
    const p = parseInt(priorityNum) || 1;
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} size={10} className={i <= p ? "text-[#FBBF24] fill-[#FBBF24]" : (isDarkMode ? "text-slate-700" : "text-slate-200")} />
        ))}
      </div>
    );
  };

  const graphicContent = (
    <div className="flex flex-col relative z-10 mb-2 w-full">
      <div className={`relative pt-10 pb-6 px-4 rounded-[2rem] border flex flex-col items-center justify-center w-full transform transition-all duration-700 ease-out ${isMounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"} ${isDarkMode ? "bg-gradient-to-br from-blue-900/60 via-slate-800 via-25% to-slate-800 border-slate-700/50 border-t-slate-600/40 shadow-[0_12px_30px_rgba(0,0,0,0.5)]" : "bg-gradient-to-br from-blue-600/20 via-white via-25% to-slate-50 border-slate-200/60 border-t-white shadow-[inset_0_2px_3px_rgba(255,255,255,1),0_12px_24px_rgba(24,119,242,0.15),0_4px_12px_rgba(0,0,0,0.01)]"}`}>
          
        <div className="absolute top-4 left-0 w-full flex justify-center pointer-events-none">
          <span className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? "text-white" : "text-black"}`}>
            Daily Operations
          </span>
        </div>

        {/* Centered Top: Radial Ring */}
        <div className="relative w-32 h-32 flex-shrink-0 mt-2 mb-5">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            <defs>
              <filter id="ringGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>
            
            <circle cx="50" cy="50" r={radius} fill="transparent" stroke={isVictoryState ? "rgba(16, 185, 129, 0.15)" : "rgba(24, 119, 242, 0.15)"} strokeWidth="16" filter="url(#ringGlow)" />
            <circle cx="50" cy="50" r={radius - 7} fill="transparent" stroke={isDarkMode ? "#334155" : "#CBD5E1"} strokeWidth="1" strokeDasharray="2, 4" opacity="0.6" />
            <circle cx="50" cy="50" r={radius} fill="transparent" stroke={isDarkMode ? "#1E293B" : "rgba(226, 232, 240, 0.9)"} strokeWidth="12" />
            
            <circle
              cx="50" cy="50" r={radius} fill="transparent"
              stroke={isVictoryState ? "#10B981" : "#1877F2"} strokeWidth="12"
              strokeDasharray={circumference} strokeDashoffset={isMounted ? strokeDashoffset : circumference}
              strokeLinecap="round" className="transition-all duration-1000 ease-out"
              filter="url(#ringGlow)"
            />
          </svg>
          <div className={`absolute inset-0 flex flex-col items-center justify-center transform transition-all duration-700 delay-300 ease-out ${isMounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
            <span className={`text-2xl font-black tracking-tighter transition-colors duration-500 leading-none ${isVictoryState ? "text-[#10B981]" : isDarkMode ? "text-white" : "text-slate-900"}`}>
              {momentumPct}%
            </span>
            <span className={`text-[8px] font-black uppercase tracking-widest mt-1 transition-colors duration-500 ${isVictoryState ? "text-[#10B981]" : isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
              COMPLETED
            </span>
          </div>
        </div>
        
        {/* Centered Middle: Task Counter Pill (Glides in from Right) */}
        <div className={`flex flex-col items-center justify-center mb-1 w-full transform transition-all duration-[800ms] delay-[300ms] ease-out ${isMounted ? "translate-x-0 opacity-100" : "translate-x-12 opacity-0"}`}>
          {isVictoryState ? (
            <div className="px-5 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.3)] animate-pulse">
              <CheckCircle2 size={24} className="text-[#10B981]" />
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <div className={`px-4 py-2 rounded-xl text-2xl font-black leading-none ${isDarkMode ? "bg-slate-800 text-[#1877F2] shadow-inner" : "bg-slate-100 text-[#1877F2]"}`}>
                {completedCount}
              </div>
              <span className={`text-lg font-black opacity-50 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>/</span>
              <div className="px-3 py-2 rounded-xl text-xl font-black leading-none bg-[#1877F2]/10 text-[#1877F2] border border-[#1877F2]/40 shadow-[0_0_12px_rgba(24,119,242,0.2)]">
                {totalTasks}
              </div>
            </div>
          )}
        </div>

        {/* Dynamic Descriptive Text: "Task Completed" vs "Tasks Completed" */}
        <div className={`mb-5 text-center pointer-events-none transform transition-all duration-[800ms] delay-[400ms] ease-out ${isMounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <span className="text-sm font-black uppercase tracking-widest text-[#1877F2]">
            {totalTasks === 1 ? "TASK COMPLETED" : "TASKS COMPLETED"}
          </span>
        </div>

        {/* Centered Bottom: Dynamic Momentum Badge (Glides up from Bottom) */}
        <div className={`px-4 py-1.5 rounded-md border text-[10px] min-[360px]:text-xs font-black tracking-widest uppercase whitespace-nowrap transform transition-all duration-[800ms] delay-[500ms] ease-out ${isMounted ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"} ${momentumBadge.classes}`}>
          {momentumBadge.text}
        </div>
        
      </div>
    </div>
  );

  const renderTaskCard = (task) => {
    return (
      <div 
        key={task.id} 
        className={`flex flex-col p-4 rounded-[1.5rem] border shadow-sm transition-all shrink-0 ${
          task.isCompleted 
            ? isDarkMode ? "bg-slate-800/40 border-slate-800 opacity-80" : "bg-slate-50 border-slate-100 opacity-80" 
            : isDarkMode ? "bg-[#1E293B] border-slate-700 hover:bg-slate-800" : "bg-white border-slate-100 hover:bg-slate-50"
        }`}
      >
        <div className="flex items-start justify-between w-full">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-12 h-12 rounded-xl border flex items-center justify-center text-xl shrink-0 ${isDarkMode ? "bg-slate-900/50 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
              {task.emoji || "📝"}
            </div>
            
            <div className="flex-1 min-w-0 pt-0.5">
              <p className={`font-bold text-sm truncate leading-tight mb-1.5 ${isDarkMode ? "text-slate-200" : "text-slate-800"} ${task.isCompleted ? "line-through" : ""}`}>
                {task.text}
              </p>
              
              <div className="flex items-center gap-2">
                {task.type === "shopping" ? (
                  <ShoppingBag size={12} strokeWidth={2.5} className="text-[#10B981] shrink-0" />
                ) : (
                  <Zap size={12} strokeWidth={2.5} className="text-[#1877F2] shrink-0" />
                )}
                {renderStars(task.priority)}
              </div>
            </div>
          </div>
          
          <button 
            onClick={(e) => { 
                e.stopPropagation(); 
                setActiveModalTodo(task);
                setEditTaskData({ text: task.text, priority: task.priority, type: task.type, emoji: task.emoji || "📝" }); 
            }} 
            className={`p-2 shrink-0 rounded-full transition-all active:scale-95 ${isDarkMode ? "hover:bg-slate-700 text-slate-500 hover:text-slate-300" : "hover:bg-slate-100 text-slate-400 hover:text-slate-600"}`}
          >
              <Edit2 size={16} strokeWidth={2.5} />
         </button>
        </div>

        <div className={`my-4 border-t ${isDarkMode ? "border-slate-700/50" : "border-slate-200"}`}></div>

        <div className="flex justify-center w-full">
          <button 
            onClick={(e) => { e.stopPropagation(); toggleTodoStatus(task.id); }}
            className={`w-full max-w-[220px] py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all active:scale-95 ${
              task.isCompleted 
                ? isDarkMode ? "bg-slate-700/50 text-slate-400 border border-slate-600" : "bg-slate-200 text-slate-500 border border-slate-300"
                : "bg-[#1877F2] text-white shadow-lg border border-transparent shadow-[0_4px_15px_rgba(24,119,242,0.3)]"
            }`}
          >
            {task.isCompleted ? <RotateCcw size={14} strokeWidth={2.5} /> : <CheckCircle2 size={14} strokeWidth={2.5} />}
            {task.isCompleted ? "Restore Task" : "Mark Completed"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`animate-fade-in pb-32 transition-colors duration-500 min-h-screen ${isDarkMode ? "bg-[#0F172A]" : "bg-[#F8FAFC]"}`}>
        
      <div className="relative z-10 Todo-Master-Header">
        <style>{`
          .Todo-Master-Header h1, 
          .Todo-Master-Header h2,
          .Todo-Master-Header h3 { 
            color: ${isDarkMode ? "#FFFFFF" : "#000000"} !important; 
            font-weight: 900 !important;
          }
        `}</style>
        {renderHeroShell(`${userName}'s Tasks`, graphicContent)}
      </div>

      <main className="px-6 space-y-6 mt-4">
        
        <div ref={addTaskRef} data-tour="todo-input" className={`p-4 rounded-[2rem] border shadow-sm ${isDarkMode ? "bg-[#1E293B] border-slate-800" : "bg-white border-slate-50"}`}>
          <div className="flex gap-2 mb-3">
            <button onClick={() => setNewTodoType("task")} className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${newTodoType === "task" ? "bg-[#1877F2] text-white shadow-md shadow-blue-500/20" : isDarkMode ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-400"}`}>To-Do</button>
            <button onClick={() => setNewTodoType("shopping")} className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${newTodoType === "shopping" ? "bg-[#10B981] text-white shadow-md shadow-emerald-500/20" : isDarkMode ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-400"}`}>To-Buy</button>
          </div>
          <form onSubmit={executeAddTodo} className="flex items-center gap-2 relative">
            
            <button 
              type="button" 
              onClick={() => {
                setIsIconSelectorOpen(!isIconSelectorOpen);
                if (!isIconSelectorOpen) {
                  setTimeout(() => addTaskRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
                }
              }}
              className={`w-12 h-12 shrink-0 rounded-xl flex items-center justify-center text-xl border transition-colors ${isDarkMode ? "bg-[#0F172A] border-slate-700 hover:bg-slate-800" : "bg-white border-slate-200 hover:bg-slate-50"}`}
            >
              {newTodoEmoji}
            </button>

            {isIconSelectorOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsIconSelectorOpen(false)}></div>
                <div className={`absolute top-14 left-0 z-50 w-full max-h-48 overflow-y-auto p-4 rounded-[1.5rem] border shadow-2xl grid grid-cols-6 gap-3 animate-fade-in [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] ${isDarkMode ? "bg-[#1E293B] border-slate-700" : "bg-white border-slate-100"}`}>
                  {categoryEmojis.map((emoji, idx) => (
                    <button key={`${emoji}-${idx}`} type="button" onClick={() => { setNewTodoEmoji(emoji); setIsIconSelectorOpen(false); }} className={`w-8 h-8 flex items-center justify-center text-xl rounded-lg transition-transform active:scale-90 hover:scale-110`}>
                      {emoji}
                    </button>
                  ))}
                </div>
              </>
            )}

            <input 
              type="text" 
              placeholder={newTodoType === "task" ? "New To-Do Task?" : "New To-Buy Task?"}
              value={newTodoText} 
              onChange={(e) => setNewTodoText(e.target.value)}
              className={`flex-1 min-w-0 py-3 px-4 rounded-xl text-sm font-bold bg-transparent border outline-none transition-colors focus:border-[#1877F2] ${isDarkMode ? "text-white border-slate-700" : "text-slate-900 border-slate-200"}`}
            />
            <button 
              type="submit" 
              disabled={!newTodoText.trim()}
              className={`w-12 h-12 shrink-0 rounded-xl flex items-center justify-center transition-all ${!newTodoText.trim() ? "bg-slate-200 text-slate-400" : "bg-[#1877F2] text-white shadow-[0_8px_16px_rgba(24,119,242,0.3)] active:scale-95"}`}
            >
              <Plus size={20} strokeWidth={3} />
            </button>
          </form>
          
          <div className="flex items-center justify-between mt-4 px-2">
             <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Priority Level</span>
             <div className="flex gap-1.5">
               {[1, 2, 3, 4, 5].map(star => (
                <Star 
                  key={star} 
                  size={24} 
                  onClick={() => setNewTodoPriority(star)}
                  className={`cursor-pointer transition-colors active:scale-90 ${star <= newTodoPriority ? "text-[#FBBF24] fill-[#FBBF24]" : (isDarkMode ? "text-slate-700 hover:text-[#FDE68A]" : "text-slate-200 hover:text-[#FDE68A]")}`} 
                 />
               ))}
            </div>
          </div>
        </div>
        
        <div className={`mx-6 my-2 border-t relative z-10 ${isDarkMode ? "border-[#FFFFFF]" : "border-slate-300"}`}></div>

        {todos.length === 0 && (
           <div className="text-center py-12 px-4">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`}>
                <CheckCircle2 size={32} className={isDarkMode ? "text-slate-600" : "text-slate-300"} />
             </div>
              <h3 className={`text-lg font-black mb-2 ${isDarkMode ? "text-white" : "text-slate-800"}`}>The slate is clean.</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-relaxed">No tasks pending.<br/>Build your empire.</p>
          </div>
        )}

        {pendingActions.length > 0 && (
          <section className={`p-4 rounded-[2rem] border shadow-sm animate-fade-in transition-colors ${isDarkMode ? "bg-blue-900/10 border-blue-900/30" : "bg-blue-50/60 border-blue-100"}`}>
            <div className="flex items-center gap-2 mb-4 px-2">
              <Zap size={16} className="text-[#1877F2]" />
              <h3 className="text-[10px] font-black uppercase tracking-widest text-[#1877F2]">
                {pendingActions.length} {pendingActions.length === 1 ? "Thing" : "Things"} To-Do
              </h3>
            </div>
            <div className="space-y-4 max-h-80 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
              {pendingActions.map(renderTaskCard)}
            </div>
          </section>
        )}

        {pendingActions.length > 0 && (pendingShopping.length > 0 || completedTasks.length > 0) && (
          <div className={`border-t relative z-10 my-4 ${isDarkMode ? "border-[#FFFFFF]" : "border-slate-300"}`}></div>
        )}

        {pendingShopping.length > 0 && (
          <section className={`p-4 rounded-[2rem] border shadow-sm animate-fade-in transition-colors ${isDarkMode ? "bg-emerald-900/10 border-emerald-900/30" : "bg-emerald-50/60 border-emerald-100"}`}>
            <div className="flex items-center gap-2 mb-4 px-2">
              <ShoppingBag size={16} className="text-[#10B981]" />
              <h3 className="text-[10px] font-black uppercase tracking-widest text-[#10B981]">
                {pendingShopping.length} {pendingShopping.length === 1 ? "Item" : "Items"} To-Buy
              </h3>
            </div>
            <div className="space-y-4 max-h-80 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
              {pendingShopping.map(renderTaskCard)}
            </div>
          </section>
        )}

        {pendingShopping.length > 0 && completedTasks.length > 0 && (
          <div className={`border-t relative z-10 my-4 ${isDarkMode ? "border-[#FFFFFF]" : "border-slate-300"}`}></div>
        )}

        {completedTasks.length > 0 && (
          <section className={`p-4 rounded-[2rem] border shadow-sm animate-fade-in transition-colors ${isDarkMode ? "bg-orange-900/10 border-orange-900/30" : "bg-orange-50/60 border-orange-100"}`}>
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#F97316]" />
                <h3 className="text-[10px] font-black uppercase tracking-widest text-[#F97316]">
                  {completedTasks.length} Completed Task{completedTasks.length === 1 ? "" : "s"}
                </h3>
              </div>
              <button 
                onClick={clearCompletedTodos}
                className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all active:scale-95 ${isDarkMode ? "bg-orange-500/20 text-orange-400 border-orange-500/30 hover:bg-orange-500/30" : "bg-orange-100 text-orange-600 border-orange-200 hover:bg-orange-200"}`}
              >
                Delete All
              </button>
            </div>
            <div className="space-y-4 max-h-80 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
              {completedTasks.map(renderTaskCard)}
            </div>
          </section>
        )}
      </main>

      {activeModalTodo && (
        <div className="absolute inset-0 z-[120] flex items-end lg:items-center lg:justify-center">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-fade-in" onClick={() => setActiveModalTodo(null)}></div>
          <div className={`w-full lg:max-w-md rounded-t-[2.5rem] lg:rounded-[2.5rem] shadow-2xl animate-slide-up relative z-[130] flex flex-col transition-colors duration-500 ${isDarkMode ? "bg-[#1E293B] border-slate-700" : "bg-white border-slate-100"}`}>
            
            <div className={`p-6 border-b flex justify-between items-center ${isDarkMode ? "border-slate-800" : "border-slate-100"}`}>
              <div className="flex items-center gap-2">
                <CheckSquare size={20} className={activeModalTodo.isCompleted ? parseInt(activeModalTodo.priority) === 5 ? "text-[#FBBF24]" : "text-[#F97316]" : "text-[#1877F2]"} />
                <h3 className={`font-black uppercase tracking-widest ${isDarkMode ? "text-white" : "text-slate-900"}`}>Edit Task</h3>
              </div>
              <button onClick={() => setActiveModalTodo(null)} className="p-2 rounded-full">
                 <X size={18} className={isDarkMode ? "text-slate-400" : "text-slate-500"} />
              </button>
            </div>
            
            <div className="p-6 space-y-4 animate-fade-in pb-[120px] lg:pb-6 relative">
              <div className="relative">
               <label className={`absolute left-4 top-2 text-[9px] font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Task Name</label>
               <input 
                 type="text" 
                 value={editTaskData.text} 
                 onChange={(e) => setEditTaskData({...editTaskData, text: e.target.value})} 
                 className={`w-full pt-6 pb-2 px-5 rounded-2xl font-bold text-sm border focus:outline-none transition-colors ${isDarkMode ? "bg-[#0F172A] border-slate-700 text-white" : "bg-slate-50 border-slate-200 text-slate-900"}`} 
                />
              </div>

              <div ref={editTaskRef} className="relative cursor-pointer" onClick={() => {
                  setIsEditIconSelectorOpen(!isEditIconSelectorOpen);
                  if (!isEditIconSelectorOpen) {
                    setTimeout(() => editTaskRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
                  }
              }}>
                  <label className={`absolute left-4 top-2 text-[9px] font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Task Icon</label>
                  <div className={`w-full pt-6 pb-2 px-5 rounded-2xl border flex items-center justify-between transition-colors ${isDarkMode ? "bg-[#0F172A] border-slate-700 text-white" : "bg-slate-50 border-slate-200 text-slate-900"}`}>
                     <span className="text-xl leading-none">{editTaskData.emoji}</span>
                     <ArrowDown size={14} className={isDarkMode ? "text-slate-400" : "text-slate-500"} />
                  </div>
              </div>

              {isEditIconSelectorOpen && (
                <div className={`absolute left-6 right-6 top-[150px] z-50 max-h-48 overflow-y-auto p-4 rounded-[1.5rem] border shadow-2xl grid grid-cols-6 gap-3 animate-fade-in [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] ${isDarkMode ? "bg-[#1E293B] border-slate-700" : "bg-white border-slate-100"}`}>
                  {categoryEmojis.map((emoji, idx) => (
                    <button key={`edit-${emoji}-${idx}`} type="button" onClick={() => { setEditTaskData({...editTaskData, emoji}); setIsEditIconSelectorOpen(false); }} className={`w-8 h-8 flex items-center justify-center text-xl rounded-lg transition-transform active:scale-90 hover:scale-110`}>
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
               <button onClick={() => setEditTaskData({...editTaskData, type: "task"})} className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${editTaskData.type === "task" ? "bg-[#1877F2] text-white shadow-md shadow-blue-500/20" : isDarkMode ? "bg-[#0F172A] text-slate-400 border border-slate-700" : "bg-white text-slate-400 border border-slate-200"}`}>To-Do</button>
               <button onClick={() => setEditTaskData({...editTaskData, type: "shopping"})} className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${editTaskData.type === "shopping" ? "bg-[#10B981] text-white shadow-md shadow-emerald-500/20" : isDarkMode ? "bg-[#0F172A] text-slate-400 border border-slate-700" : "bg-white text-slate-400 border border-slate-200"}`}>To-Buy</button>
             </div>

              <div className={`rounded-2xl p-4 border ${isDarkMode ? "bg-[#0F172A] border-slate-700" : "bg-white border-slate-200"}`}>
                <div className={`flex justify-between items-center py-2 border-b ${isDarkMode ? "border-slate-800" : "border-slate-100"}`}>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</span>
                  <span className={`text-xs font-black ${activeModalTodo.isCompleted ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
                    {activeModalTodo.isCompleted ? "Completed" : "Pending"}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Priority Level</span>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star 
                        key={star} 
                        size={24} 
                        onClick={() => setEditTaskData({...editTaskData, priority: star})}
                        className={`cursor-pointer transition-colors active:scale-90 ${star <= editTaskData.priority ? "text-[#FBBF24] fill-[#FBBF24]" : (isDarkMode ? "text-slate-700 hover:text-[#FDE68A]" : "text-slate-200 hover:text-[#FDE68A]")}`} 
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-2 space-y-3">
               <button 
                  onClick={handleSaveEdit} 
                  className="w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-white bg-[#1877F2] shadow-[0_8px_16px_rgba(24,119,242,0.3)] transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <Save size={16} /> Save Changes
               </button>
               <button 
                  onClick={handleDeleteTask} 
                  className="w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-white bg-red-500 shadow-[0_8px_16px_rgba(239,68,68,0.3)] transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <Trash2 size={16} /> Delete Task
               </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
